'use client';

import { createContext, ReactNode, useContext } from "react";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SiteAuthenticPlaces, SiteAuthenticPlacesInput } from "@/app/lib/generated/graphql-types";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { SiteAuthenticPlacesEditable } from "@/app/features/sites/authentic-places/types/types";
import { SiteAuthenticPlacesSchema } from "@/app/features/sites/authentic-places/types/schema";
import { getSiteAuthenticPlaces } from "@/app/features/sites/authentic-places/data-access/queries";
import { updateSiteAuthenticPlaces } from "@/app/features/sites/authentic-places/data-access/mutations";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";

type SiteAuthenticPlacesEditorContextType = UseSingleTypeEditorType<SiteAuthenticPlacesEditable>;

const SiteAuthenticPlacesEditorContext = createContext<SiteAuthenticPlacesEditorContextType | undefined>(undefined);

export function useSiteAuthenticPlacesEditorContext() {
    const context = useContext(SiteAuthenticPlacesEditorContext);
    if (!context) {
        throw new Error("useSiteAuthenticPlacesEditorContext must be used within a SiteAuthenticPlacesEditorProvider");
    }
    return context;
}

interface SiteAuthenticPlacesEditorProviderProps {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}

export default function SiteAuthenticPlacesEditorProvider({ children, isDraftModeEnabled }: SiteAuthenticPlacesEditorProviderProps) {
    const editor = useSingleTypeEditor<SiteAuthenticPlaces, SiteAuthenticPlacesEditable, SiteAuthenticPlacesInput>({
        schema: SiteAuthenticPlacesSchema,
        loadEntity: getSiteAuthenticPlaces,
        update: updateSiteAuthenticPlaces,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteAuthenticPlacesEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => {
                    void editor.discardChanges();
                }}
            />
        </SiteAuthenticPlacesEditorContext.Provider>
    )
}
